/**
 * Carrossel horizontal genérico da home da plataforma.
 *
 * Usa scroll nativo com scroll-snap em vez da matemática de `translateX` do
 * ModuleCarousel: aqui os cards precisam sangrar para fora da borda direita e a
 * quantidade visível varia com o breakpoint. Scroll nativo também dá swipe em
 * touch e navegação por teclado de graça.
 *
 * As setas são faixas de altura total nas laterais, com fade para o fundo, e só
 * aparecem no hover — o desenho do mockup.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';

interface CourseCarouselProps {
  title?: string;
  children: React.ReactNode;
  /** Largura de cada item. Por padrão quem manda no tamanho é o próprio card. */
  itemClassName?: string;
}

/** Recuo lateral do mockup, igual no cabeçalho e no track. */
const EDGE = 'clamp(20px,5vw,64px)';

const arrowBase =
  `absolute inset-y-0 z-[5] hidden w-[clamp(48px,6vw,72px)] place-items-center text-mv-text opacity-0 transition-opacity duration-200
   pointer-events-none md:grid
   group-hover/carousel:pointer-events-auto group-hover/carousel:opacity-100
   focus-visible:pointer-events-auto focus-visible:opacity-100 focus:outline-none
   disabled:pointer-events-none disabled:opacity-0`;

const CourseCarousel: React.FC<CourseCarouselProps> = ({
  title,
  children,
  itemClassName = '',
}) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    // Tolerância de 1px: scrollWidth/clientWidth são arredondados e o "fim"
    // raramente bate exato.
    setCanPrev(el.scrollLeft > 1);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  // Observa o track uma única vez. `children` fora das dependências de
  // propósito: é um array novo a cada render, e mantê-lo aqui recriaria o
  // listener e o ResizeObserver em todo ciclo sem necessidade.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    updateArrows();

    el.addEventListener('scroll', updateArrows, { passive: true });
    const observer = new ResizeObserver(updateArrows);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateArrows);
      observer.disconnect();
    };
  }, [updateArrows]);

  // Reavalia quando a quantidade de itens muda. A contagem é um número — uma
  // dependência estável, ao contrário do array de children.
  const itemCount = React.Children.count(children);
  useEffect(() => {
    updateArrows();
  }, [itemCount, updateArrows]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;

    // ~82% da largura visível: avança quase uma tela cheia mantendo um card de
    // referência à vista. Mínimo de 260px para faixas estreitas.
    const step = Math.max(el.clientWidth * 0.82, 260);

    // `scroll-behavior: auto` no CSS não alcança scrollBy programático: quem
    // pede movimento reduzido continuaria vendo a animação.
    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    el.scrollBy({ left: direction * step, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  return (
    <section>
      {title && (
        <div
          className="mb-[14px] flex items-baseline gap-3"
          style={{ paddingInline: EDGE }}
        >
          <h2 className="font-display text-[19px] font-bold tracking-[-0.01em] text-mv-text">
            {title}
          </h2>
        </div>
      )}

      <div className="group/carousel relative">
        <button
          type="button"
          onClick={() => scrollByPage(-1)}
          disabled={!canPrev}
          aria-label={t('platform.carousel.prev')}
          className={`${arrowBase} left-0 bg-gradient-to-r from-mv-bg to-transparent`}
        >
          <ChevronLeftIcon className="h-[26px] w-[26px] [filter:drop-shadow(0_2px_8px_rgba(0,0,0,.6))] transition-colors hover:text-mv-accent" />
        </button>

        <div
          ref={trackRef}
          className="no-scrollbar flex snap-x snap-proximity gap-[14px] overflow-x-auto scroll-smooth py-1.5"
          style={{ paddingInline: EDGE, scrollPaddingInline: EDGE }}
        >
          {React.Children.map(children, (child, i) => (
            <div key={i} className={`shrink-0 snap-start ${itemClassName}`}>
              {child}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() => scrollByPage(1)}
          disabled={!canNext}
          aria-label={t('platform.carousel.next')}
          className={`${arrowBase} right-0 bg-gradient-to-l from-mv-bg to-transparent`}
        >
          <ChevronRightIcon className="h-[26px] w-[26px] [filter:drop-shadow(0_2px_8px_rgba(0,0,0,.6))] transition-colors hover:text-mv-accent" />
        </button>
      </div>
    </section>
  );
};

export default CourseCarousel;
